







import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import i18n from "@/i18n";
import * as analysesService from "@/services/analyses";
import * as monitoringService from "@/services/monitoring";
import { CropsDTO, CropsYieldEvolutionDTO } from "../../../../api/crops/types";
import {
  AnalysisYieldEvolutionDTO,
  LastYearYieldDTO,
} from "@/api/analyses/types";
import { ChartColors, ChartConstants } from "@/constants/chart";
import ResizeObserver from 'resize-observer-polyfill';

@Component({
  name: "YieldEvolutionChartHc",
})
export default class YieldEvolutionChartHc extends Vue {
  @Prop({ type: Boolean, default: false })
  private completed!: boolean;

  @Prop({ type: String, required: true })
  private uuid!: string;

  @Prop({ type: String, required: true })
  private sentinelaUuid!: string;

  @Prop({ type: String, required: true })
  private harvestSeason!: string;

  @Prop({ type: String, required: true })
  private cropType!: string;

  @Prop({ type: Array, default: () => [] })
  private crops!: string[];

  private ro: ResizeObserver | null = null;

  private chartOptions: any = {};

  private beforeMount() {
    this.chartOptions = {
      chart: {
        backgroundColor: "#12191c",
        style: {
          fontFamily: ChartConstants.FONT_FAMILY,
        },
      },

      title: {
        text: this.$t("monitoring.evolution-chart.chart-label") as string,
      },

      credits: {
        enabled: false,
      },

      yAxis: {
        title: {
          enabled: false,
        },
        softMax: 65,
        softMin: 45,
      },

      xAxis: {
        type: "datetime",
      },

      tooltip: {
        xDateFormat: "%d/%m/%Y",
        shared: true,
      },

      legend: {
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
      },

      plotOptions: {
        series: {
          marker: {
            enabledThreshold: 0,
          },
          label: {
            connectorAllowed: false,
          },
        },
      },

      series: this.loadedData,

      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              legend: {
                layout: "horizontal",
                align: "center",
                verticalAlign: "bottom",
              },
            },
          },
        ],
      },
    };
  }

  private mounted() {
    this.redrawChartOnWillAppear();
  }

  @Watch("loadedData", { immediate: true })
  private dataChanged(data: CropsYieldEvolutionDTO) {
    requestAnimationFrame(() => {
      this.chartOptions.series = data;
    });
  }

  @Watch("uuid", { immediate: true })
  private async mountYieldEvolutionBaseData() {
    await Promise.all([
      await this.mountAnalysisYieldEvolutionData(),
      await this.mountLastYearYieldData(),
    ]);
  }

  @Watch("crops", { immediate: true })
  private mountCropsData(crops: string[]) {
    crops.forEach((cropUuid) => {
      requestAnimationFrame(async () => {
        await monitoringService.fetchCropYieldEvolution(
          cropUuid,
          this.harvestSeason,
          this.cropType
        );
      });
    });
  }

  @Watch("hovered", { immediate: true })
  private onHover(cropLabel: string | null) {
    if (!!cropLabel) {
      this.$emit("update:hover", this.invCropsMapper.get(cropLabel));
    } else {
      this.$emit("update:hover", null);
    }
  }

  private get cropsMapper() {
    const crops = (this.$store.getters[`crops/crops`](this.sentinelaUuid) ??
      []) as CropsDTO[];
    return new Map(crops.map((crop) => [crop.cropUuid, `${crop.cropLabel}`]));
  }

  private get invCropsMapper() {
    const crops = (this.$store.getters[`crops/crops`](this.sentinelaUuid) ??
      []) as CropsDTO[];
    return new Map(crops.map((crop) => [`${crop.cropLabel}`, crop.cropUuid]));
  }

  private get loadedData(): any {
    const cropYieldEvolutionDataGetter = this.$store.getters[
      "monitoring/cropsYieldEvolution"
    ] as (uuid: string) => CropsYieldEvolutionDTO[];

    return [
      {
        name: this.$t("monitoring.evolution-chart.chart-label") as string,
        data: this.analysisYieldEvolutionData.map((aye) => ({
          x: Date.UTC(
              aye.sceneDate.getFullYear(),
              aye.sceneDate.getMonth(),
              aye.sceneDate.getDate()
            ),
          y: this.roundTwoPlaces(aye.average),
        })),
        color: ChartColors.GREEN,
        events: {
          mouseOver: function () {
            // @ts-ignore
            this.update({
              color: ChartColors.LINE_HOVER,
            });
          },
          mouseOut: function () {
            // @ts-ignore
            this.update({
              color: ChartColors.GREEN,
            });
          },
        },
      },
      this.lastYearYieldData !== null
        ? {
            data: this.analysisYieldEvolutionData.map((aye) => ({
              x: Date.UTC(
                aye.sceneDate.getFullYear(),
                aye.sceneDate.getMonth(),
                aye.sceneDate.getDate()
              ),
              y: this.roundTwoPlaces(
                (this.lastYearYieldData as LastYearYieldDTO).yield
              ),
            })),
            name: this.$t(
              "monitoring.evolution-chart.last-year-label"
            ) as string,
            color: ChartColors.WHITE,
            events: {
              mouseOver: function () {
                // @ts-ignore
                this.update({
                  color: ChartColors.LINE_HOVER,
                });
              },
              mouseOut: function () {
                // @ts-ignore
                this.update({
                  color: ChartColors.WHITE,
                });
              },
            },
          }
        : {
            data: null,
            name: "",
          },

      ...this.crops.map((cropUuid) => {
        return {
          data: cropYieldEvolutionDataGetter(cropUuid).map((cyd) => ({
            x: Date.UTC(
                cyd.sceneDate.getFullYear(),
                cyd.sceneDate.getMonth(),
                cyd.sceneDate.getDate()
              ),
            y: this.roundTwoPlaces(cyd.value),
          })),
          name: `${this.cropsMapper.get(cropUuid) ?? cropUuid}`,
          color: ChartColors.WHITE,
          events: ((vm) => {
            return {
              mouseOver: function () {
                // @ts-ignore
                this.update({
                  color: ChartColors.OFF_WHITE,
                });
                vm.$emit("update:hover", cropUuid);
              },
              mouseOut: function () {
                // @ts-ignore
                this.update({
                  color: ChartColors.WHITE,
                });
                vm.$emit("update:hover", null);
              },
            };
          })(this),
        };
      }),
    ];
  }

  private roundTwoPlaces(num: number) {
    return Math.round((num + Number.EPSILON) * 100) / 100;
  }

  private get analysisYieldEvolutionData(): AnalysisYieldEvolutionDTO[] {
    return this.$store.state.analyses.analysisYieldEvolutionContext ?? [];
  }

  private get lastYearYieldData(): LastYearYieldDTO | null {
    return this.$store.state.analyses.lastYearYieldContext;
  }

  private async mountAnalysisYieldEvolutionData() {
    await analysesService.getFarmAnalysisYieldEvolution(
      this.sentinelaUuid,
      this.harvestSeason,
      this.cropType
    );
  }

  private async mountLastYearYieldData() {
    await analysesService.getLastYearYieldData(
      this.uuid,
      this.harvestSeason,
      this.cropType
    );
  }

  private redrawChartOnWillAppear() {
    let lastHeight = 0;
    let lastWidth = 0;

    this.ro = new ResizeObserver((entries, observer) => {
      for (const entry of entries) {
        const { height, width} = entry.contentRect;
        if ((height !== lastHeight  || width !== lastWidth)) {
          requestAnimationFrame(() => {
            this.reflow();
          });
        }
        lastHeight = height;
        lastWidth = width
      }
    });
    this.ro.observe((this.$refs.hcComponent! as Vue).$el);
  }

  public reflow() {
    // @ts-ignore
    this.$refs.hcComponent.chart.reflow();
  }
}
